import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ParentData } from "features/cases/blocks/CommonCard/CommonCard.types";
import {
  applySavedItemToCache,
  postCardContent,
} from "features/cases/blocks/CommonCard/CommonCard.utils";
import { buildCardSavePayload } from "features/cases/blocks/CommonCard/hooks/useCardSave";
import { useCallback, useEffect, useRef, useState } from "react";
import { hasMinimalAutosaveContent } from "utils/card-content-validation";

export type AutosaveStatus = "idle" | "dirty" | "saving" | "saved" | "error";

type Snapshot = {
  title: string;
  description: string;
  sphere: number | null;
  variant: number | null;
};

const snapshotsEqual = (a: Snapshot, b: Snapshot): boolean =>
  a.title === b.title &&
  a.description === b.description &&
  a.sphere === b.sphere &&
  a.variant === b.variant;

// Правки существующей записи дебаунсятся короче, чем создание — лишний
// черновик расходует дневной лимит, поэтому первый тик делаем медленнее.
const EDIT_DEBOUNCE_MS = 1500;
const CREATE_DEBOUNCE_MS = 3500;
const MAX_WAIT_MS = 15000;

type UseAutosaveParams = {
  enabled: boolean;
  hasExistingId: boolean;
  editContentUrl: string;
  queryId: string | undefined;
  parentData: ParentData;
  titleInput: string;
  editedContent: string;
  selectedSphere: number | null;
  selectedVariant: number | null;
  isUsingCollectivePerson: boolean;
  personId: number | null | undefined;
  onCreated: (newId: string) => void;
};

// Автосохранение сохраняет на backend только черновики/новый контент (как
// ручное "В черновик") — isDraft/publish и модалку не трогает.
export const useAutosave = ({
  enabled,
  hasExistingId,
  editContentUrl,
  queryId,
  parentData,
  titleInput,
  editedContent,
  selectedSphere,
  selectedVariant,
  isUsingCollectivePerson,
  personId,
  onCreated,
}: UseAutosaveParams) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const snapshotRef = useRef<Snapshot>({
    title: titleInput,
    description: editedContent,
    sphere: selectedSphere,
    variant: selectedVariant,
  });

  // Точка отсчёта для "Отмены": исходное содержимое при открытии редактора,
  // либо последняя явно сохранённая версия (resyncSnapshot) — в отличие от
  // snapshotRef, хранящего последний тик автосохранения.
  const originalSnapshotRef = useRef<Snapshot>({
    title: titleInput,
    description: editedContent,
    sphere: selectedSphere,
    variant: selectedVariant,
  });

  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const maxWaitTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      postCardContent(editContentUrl, payload),
    onMutate: () => setStatus("saving"),
    onSuccess: (updatedItem, payload) => {
      snapshotRef.current = {
        title: String(payload.title ?? ""),
        description: String(payload.description ?? ""),
        sphere: (payload.sphere as number | null) ?? null,
        variant: (payload.variantId as number | null) ?? null,
      };
      setStatus("saved");
      setLastSavedAt(new Date());

      if (!hasExistingId && updatedItem?.id) {
        onCreated(String(updatedItem.id));
      }

      applySavedItemToCache(queryClient, parentData, queryId, updatedItem);
    },
    onError: () => setStatus("error"),
  });

  const flushNow = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(maxWaitTimerRef.current);
    maxWaitTimerRef.current = undefined;

    // Хук монтируется всегда, даже когда backend выключен (например, правится
    // уже опубликованная строка). flushNow вызывается из beforeunload/unmount
    // напрямую, минуя проверку enabled — иначе правки ушли бы на backend.
    if (!enabled) return;

    const current: Snapshot = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };

    if (snapshotsEqual(current, snapshotRef.current)) return;
    if (
      !hasExistingId &&
      !hasMinimalAutosaveContent({
        title: current.title,
        description: current.description,
      })
    ) {
      return;
    }

    mutation.mutate(
      buildCardSavePayload({
        editedContent: current.description,
        selectedSphere: current.sphere,
        selectedVariant: current.variant,
        titleInput: current.title,
        parentData,
        isUsingCollectivePerson,
        personId,
        isDraftOverride: hasExistingId ? undefined : true,
      }),
    );
  }, [
    enabled,
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
    hasExistingId,
    parentData,
    isUsingCollectivePerson,
    personId,
  ]);

  // Явное сохранение уже отправило эти значения на backend — подтягиваем
  // снимок, чтобы автосохранение не повторило запрос, и сдвигаем точку
  // отсчёта "Отмены" на эту версию: откат должен затрагивать только правки
  // ПОСЛЕ этого сохранения.
  const resyncSnapshot = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(maxWaitTimerRef.current);
    maxWaitTimerRef.current = undefined;
    const current: Snapshot = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };
    snapshotRef.current = current;
    originalSnapshotRef.current = current;
    setStatus("idle");
    setLastSavedAt(new Date());
  }, [titleInput, editedContent, selectedSphere, selectedVariant]);

  // Пользователь подтвердил закрытие без сохранения. За время редактирования
  // автосохранение могло уже отправить правки на backend — просто отменить
  // отложенный тик недостаточно, нужно откатить запись к виду на момент
  // открытия (originalSnapshotRef), иначе "Отмена" не отменит то, что уже
  // само сохранило автосохранение. Сначала синхронизируем snapshotRef с
  // текущими значениями — иначе unmount-эффект (flushNow при закрытии
  // модалки) увидит расхождение и переотправит их, перезаписав откат.
  const cancelPendingChanges = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(maxWaitTimerRef.current);
    maxWaitTimerRef.current = undefined;

    const original = originalSnapshotRef.current;
    const current: Snapshot = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };

    snapshotRef.current = current;

    if (hasExistingId && !snapshotsEqual(current, original)) {
      mutation.mutate(
        buildCardSavePayload({
          editedContent: original.description,
          selectedSphere: original.sphere,
          selectedVariant: original.variant,
          titleInput: original.title,
          parentData,
          isUsingCollectivePerson,
          personId,
          isDraftOverride: undefined,
        }),
      );
    }

    setStatus("idle");
  }, [
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
    hasExistingId,
    parentData,
    isUsingCollectivePerson,
    personId,
    mutation,
  ]);

  useEffect(() => {
    if (!enabled) return undefined;

    const current: Snapshot = {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    };

    if (snapshotsEqual(current, snapshotRef.current)) return undefined;

    setStatus("dirty");

    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(
      flushNow,
      hasExistingId ? EDIT_DEBOUNCE_MS : CREATE_DEBOUNCE_MS,
    );

    if (!maxWaitTimerRef.current) {
      maxWaitTimerRef.current = setTimeout(flushNow, MAX_WAIT_MS);
    }

    return () => clearTimeout(idleTimerRef.current);
  }, [
    enabled,
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
    hasExistingId,
    flushNow,
  ]);

  // ModalWindow размонтирует редактор при закрытии, а не скрывает —
  // незавершённый дебаунс иначе пропал бы молча. Коммитим через ref, чтобы
  // не попасть на устаревшее замыкание.
  const flushNowRef = useRef(flushNow);
  flushNowRef.current = flushNow;

  useEffect(
    () => () => {
      clearTimeout(idleTimerRef.current);
      clearTimeout(maxWaitTimerRef.current);
      flushNowRef.current();
    },
    [],
  );

  // Закрытие вкладки не размонтирует React — нужен отдельный шанс сохранить
  // перед уходом со страницы.
  useEffect(() => {
    const handler = () => flushNowRef.current();

    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, []);

  // Сравнение с точкой отсчёта, а не статус тика: даже ушедшее на backend
  // автосохранение "Отмена" должна откатить — диалог подтверждения нужен и
  // здесь, не только при неотправленном тике.
  const hasUnsavedChanges = !snapshotsEqual(
    {
      title: titleInput,
      description: editedContent,
      sphere: selectedSphere,
      variant: selectedVariant,
    },
    originalSnapshotRef.current,
  );

  return {
    status,
    lastSavedAt,
    hasUnsavedChanges,
    flushNow,
    resyncSnapshot,
    cancelPendingChanges,
  };
};
